/**
 * User: WangMeng
 * Date: 2018-05-17
 * Time: 18:07
 * Desc: 监管统计图形数据格式化
 */
import { MEDIAN_SCORE } from '../../../../constants/common';
import { numberFixed, provinceNameFormat, wordLinefeed } from '../../../../utils/formatter';
import { isArray } from '../../../../utils/util';
import { getAxisMax, getScoreAxisMax, calcSymbolSize, completeMapData } from '../../../../utils/chart';
import { colors } from '../../../common/echarts/config/config';

function isInvalidData(data) {
    return !(isArray(data) && data.length);
}

// 地图
export function formatGeoMapOption(data) {
    if(isInvalidData(data)) {
        return {};
    }

    let values = data.map(d => {
        d.name = provinceNameFormat(d.provinceName);
        d.companyCount = d.companyCount || 0;
        d.highCompanyCount = d.highCompanyCount || 0;
        d.value = numberFixed(d.avgScore);
        return d.value;
    });

    let max = Math.max.apply(null, values);
    let min = Math.min.apply(null, values);

    data = completeMapData(data);

    return {
        tooltip: {
            formatter: function (param) {
                return param.data && param.data.provinceName ?
                    `<div>
                                    <p>${param.name}地区上市公司<span class="orange-font">${param.data.companyCount}</span>家</p>
                                    <p><span class="orange-font">${param.data.highCompanyCount}</span>家上市公司财务风险较大</p>
                                    <p>地区平均财务风险${param.data.value >= MEDIAN_SCORE ? '较大' : '较小'}</p>
                            </div> ` : `${param.name}: --`;
            }
        },
        visualMap: {
            max: max === min ? max + 0.01 : max, // 防止最大值和最小值相等
            min,
            precision: 2,
            hoverLink: false
        },
        series: [{ name: '平均分值', data }]
    };
}

// 走势图
export function formatGeoTrendOption(data, fields = []) {
    if(isInvalidData(data)) {
        return {};
    }
    let category = [], scoreData = [], companyData = [];
    data.forEach(d => {
        let score = numberFixed(d[fields[1]]);
        category.push(d[fields[0]]);
        scoreData.push(score);
        companyData.push(d[fields[2]] || 0);
    });

    let maxCount = Math.max.apply(null, companyData) || 0,
        maxScore = Math.max.apply(null, scoreData) || 0,
        maxYAxis1 = getScoreAxisMax(maxScore),
        maxYAxis2 = getAxisMax(maxCount);

    if (!scoreData.length && !companyData.length) {
        return {};
    }
    return {
        color: ['#F99248', '#3370C8'],
        grid: {
            top: 25,
            right: 12
        },
        yAxis: [{
            max: maxYAxis1,
            interval: maxYAxis1 * 100 / 5 / 100
            // splitNumber: 5
        }, {
            max: maxYAxis2,
            interval: maxYAxis2 / 5,
            axisLine: {
                show: true
            },
            axisLabel: {
                show: true
            },
            splitLine: {
                show: true
            }
        }],
        category: category,
        lineData: [
            { name: '评分均值', data: scoreData },
            { name: '高风险公司数目', yAxisIndex: 1, data: companyData }
        ]
    };
}

// 饼图
export function formatGeoPieData(data, fields = [],  tooltip) {
    if(isInvalidData(data)) {
        return {};
    }
    data.forEach(d => {
        d.name = d[fields[0]];
        d.value = d[fields[1]];
        d.score = numberFixed(d[fields[2]], null);
    });

    return {
        color: ['#F8B551', '#F99248', '#73CDFC', '#90BBF8', '#2365C4', '#534FCF', '#AAA7F9', '#ACD598', '#6CBA52', '#097C25', '#ED726C'],
        legend: {
            type: 'scroll',
            left: '45%'
        },
        tooltip: {
            formatter: tooltip
        },
        center: ['25%', '50%'],
        data
    };
}

// 散点图
export function formatIndScatterData(data) {
    if(isInvalidData(data)) {
        return {};
    }

    let values = [], category = [];

    data.forEach((d, i) => {
        let score = numberFixed(d.industryAverage);
        d.value = [i, score];
        category.push(d.industryName);
        values.push(score);
    });

    let maxValue = Math.max.apply(null, values) || 0,
        minValue = Math.min.apply(null, values) || 0,
        maxYAxis = getScoreAxisMax(maxValue);

    calcSymbolSize(data, 'industryCompanyCount', [20, 60]);

    return {
        grid: {
            top: 30,
            right: 140,
            bottom: 70
        },
        tooltip: {
            formatter: function (param) {
                return `行业：${param.name}<br />
                            行业评分均值：${param.data.value[1]}<br />
                            行业公司数目：<span class="orange-font">${param.data.industryCompanyCount}</span>家<br />
                            <span class="orange-font">${param.data.exceedTotal}</span>家上市公司财务风险较大<br />
                            行业平均财务风险${param.data.value[1] > MEDIAN_SCORE ? '较大' : '较小'}`;
            }
        },
        visualMap: {
            show: false,
            max: maxValue === minValue ? maxValue + 0.01 : maxValue, // 防止最大值和最小值相等
            min: minValue,
            dimension: 1,
            inRange: {
                color: ['#9CDAFB', '#14ADFF']
            }
        },
        dataZoom: [
            {
                show: true,
                zoomLock: true,
                startValue: 0,
                endValue: 5,
                height: 20,
                left: 50,
                right: 160,
                bottom: 10,
                labelFormatter: function (v, d) {
                    return wordLinefeed(d, v < 2 ? 6 : 8);
                }
            }
        ],
        xAxis: [{
            type: 'category',
            data: category,
            axisLabel: {
                rotate: -30,
                formatter: function (v) {
                    return wordLinefeed(v, 8);
                }
            }
        }],
        yAxis: [{
            max: maxYAxis,
            interval: maxYAxis * 100 / 5 / 100
            // splitNumber: 5
        }],
        data
    };
}

// 分数分布柱状图
export function formatScoreDistOption(data) {
    if(isInvalidData(data)) {
        return {};
    }
    let category = [], barData = [];
    data.forEach(d => {
        category.push({ value: d.name, type: d.type });
        barData.push(d.count);
    });

    return {
        color: ['#FF4545'],
        grid: {
            top: 35,
            right: 50,
            left: 5
        },
        visualMap: {
            show: true,
            top: 0,
            right: 50,
            orient: 'horizontal',
            itemWidth: 10,
            itemHeight: 80,
            max: category.length - 1,
            min: 0,
            dimension: 0,
            hoverLink: false,
            text: ['高风险', '低风险'],
            inRange: {
                color: ['#F6F1B3', '#FF4545']
            },
            textStyle: {
                fontSize: 14,
                color: colors.textColor
            }
        },
        xAxis: [{
            data: category,
            axisLabel: {
                rotate: 60
            },
            triggerEvent: true
        }],
        barData
    };
}

// 分析概览走势图
export function formatComTrendOption(data) {
    if(isInvalidData(data)) {
        return {};
    }
    let category = [],
        companyData = [],
        industryData = [];

    data.forEach(d => {
        category.push(d.report_date);
        companyData.push(numberFixed(d.company_score));
        industryData.push(numberFixed(d.industry_score));
    });

    if (!companyData.length && !industryData.length) {
        return {};
    }

    let maxValue = Math.max.apply(null, companyData.concat(industryData)) || 0,
        maxYAxis = getScoreAxisMax(maxValue);

    return {
        color: ['#73CDFC', '#F99248'],
        grid: {
            top: 25,
            right: 15
        },
        yAxis: [{
            max: maxYAxis,
            interval: maxYAxis * 100 / 5 / 100
        }],
        category: category,
        lineData: [
            { name: '公司', data: companyData },
            { name: '行业', data: industryData }
        ]
    };
}

// 词云图
export function formatStaWordOption(data) {
    if(isInvalidData(data)) {
        return {};
    }
    let color = ['#1B6BA4', '#7FBB75', '#D68080', '#A8AA21', '#2365C4', '#B4B576', '#9CB3D3', '#8DC5D1'];
    data.forEach((d, i) => {
        d.name = d.sesName;
        d.value = d.count;
        d.textStyle = { normal: { color: color[i % color.length] } };
    });
    return {
        series: [{
            gridSize: 30,
            data
        }]
    };
}

// 统计信息柱状图
export function formatStaBarOption(data) {
    if(isInvalidData(data)) {
        return {};
    }

    let label = {
        show: true,
        position: 'inside',
        fontSize: 12,
        color: '#fff',
        formatter: (d) => d.value || ''
    };
    let barData = [
        { name: '交易所问询函', data: [], label },
        { name: '证监局行政监管措施', data: [], label },
        { name: '证监会行政处罚', data: [], label },
        { name: '交易所监管函', data: [], label }
    ];
    let category = [];

    data.forEach((d) => {
        category.push(d.name);
        barData[0].data.push(d.askLetter);
        barData[1].data.push(d.supervisionMeasure);
        barData[2].data.push(d.supervisionPubnish);
        barData[3].data.push(d.supervisionLetter);
    });

    barData.forEach(d => {
        d.data = d.data.map(d => d === 0 ? ({ value: d, tooltip: { show: false } }) : d);
    });

    return {
        color: ['#2365C4', '#F99248', '#FACD89', '#A9DFFB'],
        yAxis: [{
            axisLine: {
                show: false
            },
            axisLabel: {
                show: false
            },
            splitLine: {
                show: false
            }
        }],
        grid: {
            top: 25
        },
        legend: {
            left: 'center',
            itemWidth: 10,
            itemHeight: 10
        },
        barStack: 'count',
        barMaxWidth: 60,
        category,
        barData
    };
}



// WEBPACK FOOTER //
// ./src/components/regulatoryStatistical/index/common/formatEchartOption.js